import { useTranslation } from "@next-platform/client-common-core";
import { RulebookListCardStatus } from "@next-platform/client-common-core/rulebook/cards/RulebookListCardStatus";
import { Icon, IconSize, IconVariants, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import React, { memo, useMemo } from "react";

import Trans from "@/components/Trans/Trans";

import RulebookListCardStatusPill from "./RulebookListCardStatusPill";

type StatCountProps = {
  count: number;
  kind: "rules" | "bots" | "personas";
  testId: string;
};

const STAT_ICONS: Record<StatCountProps["kind"], IconVariants> = {
  rules: IconVariants.CLIPBOARD_CHECK,
  bots: IconVariants.BOT,
  personas: IconVariants.USER,
};

type StatLabelProps = {
  count: number;
  kind: StatCountProps["kind"];
  defaults: string;
  values: { count: number };
};

const renderStatLabel = ({ count, kind, defaults, values }: StatLabelProps) => {
  switch (kind) {
    case "rules":
      return <Trans i18nKey="rulebook:card.rules" defaults={defaults} count={count} values={values} />;
    case "bots":
      return <Trans i18nKey="rulebook:card.bots" defaults={defaults} count={count} values={values} />;
    case "personas":
      return <Trans i18nKey="rulebook:card.personas" defaults={defaults} count={count} values={values} />;
    default:
      return null;
  }
};

const StatCount = ({ count, kind, testId }: StatCountProps) => {
  const defaults = useMemo(() => {
    const countLabel = count === 1 ? "rule" : "rules";
    if (kind === "bots") {
      return `{{count}} ${count === 1 ? "bot" : "bots"}`;
    }
    if (kind === "personas") {
      return `{{count}} ${count === 1 ? "persona" : "personas"}`;
    }
    return `{{count}} ${countLabel}`;
  }, [count, kind]);
  const values = useMemo(() => ({ count }), [count]);
  const label = renderStatLabel({ count, kind, defaults, values });

  return (
    <span className="inline-flex items-center gap-1" data-testid={testId}>
      <Icon icon={STAT_ICONS[kind]} size={IconSize.XSMALL} />
      <Text as="span" variant={TextVariants.LABEL_SMALL_REGULAR}>
        {label}
      </Text>
    </span>
  );
};

type RulebookListCardStatsProps = {
  status?: RulebookListCardStatus;
  numRules: number;
  numBots: number;
  numPersonas: number;
  /** Public cards only: the author shares the full definition, so viewers can view/clone it. */
  isDefinitionVisible?: boolean;
  "data-testid"?: string;
};

const RulebookListCardStats = ({
  status,
  numRules,
  numBots,
  numPersonas,
  isDefinitionVisible = false,
  "data-testid": testId = "RulebookListCardStats",
}: RulebookListCardStatsProps) => {
  const { t: translate } = useTranslation();
  return (
    <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1.5 pt-1 text-gray-10" data-testid={testId}>
      {status ? <RulebookListCardStatusPill status={status} /> : null}
      <StatCount count={numRules} kind="rules" testId="RulebookListCard-Rules" />
      {numBots > 0 ? <StatCount count={numBots} kind="bots" testId="RulebookListCard-Bots" /> : null}
      {numPersonas > 0 ? <StatCount count={numPersonas} kind="personas" testId="RulebookListCard-Personas" /> : null}
      {isDefinitionVisible ? (
        <span
          className="inline-flex items-center gap-1 text-green-11"
          title={translate("rulebook:card.definitionVisibleHint", "You can view and clone this Rulebook's full definition")}
          data-testid="RulebookListCard-DefinitionVisible"
        >
          <Icon icon={IconVariants.EYE} size={IconSize.XSMALL} />
          <Text as="span" variant={TextVariants.LABEL_SMALL_REGULAR}>
            <Trans i18nKey="rulebook:card.definitionVisible" defaults="Definition visible" />
          </Text>
        </span>
      ) : null}
    </div>
  );
};

export default memo(RulebookListCardStats);
